import { useNavigate } from 'react-router-dom'

import { TwoColumnLayout } from '@factorialco/f0-react'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import { ContactFragment } from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment from 'moment'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { ContactsDetailsInfo } from 'modules/finance/components/Contacts/ContactsDetailsInfo'
import { buildContactsInvoicesWidget } from 'modules/finance/components/Contacts/Widgets/InvoiceWidget'
import { buildProjectsWidget } from 'modules/finance/components/Contacts/Widgets/ProjectWidget'
import { buildContactsPurchaseOrdersWidget } from 'modules/finance/components/Contacts/Widgets/PurchaseOrderWidget'
import { buildTasksWidget } from 'modules/finance/components/Contacts/Widgets/TaskWidget'
import { WidgetDashboard } from 'modules/profile/components/Insights/widgets'

interface OverviewInformationProps {
  contact: ContactFragment
  canSeeProjects: boolean
  canSeePurchaseOrders: boolean
  canManageFinancialDocuments: boolean
  contactType: FinanceValueObjectsContactTypeEnum
}

const OverviewInformation: React.FC<OverviewInformationProps> = ({
  contact,
  canSeeProjects,
  canSeePurchaseOrders,
  canManageFinancialDocuments,
  contactType,
}) => {
  const { employee } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  if (!employee) {
    return <NotFound />
  }

  const handleEditContact = () => {
    if (contactType === 'client') {
      navigate(navigation.finance.sales.clients.contactId.edit.fullPath({ contactId: contact.id }))
    } else {
      navigate(
        navigation.finance.spending.vendors.contactId.edit.fullPath({ contactId: contact.id })
      )
    }
  }

  const widgetsList = compact([
    canSeeProjects ? buildProjectsWidget(contactType) : null,
    canSeeProjects ? buildTasksWidget() : null,
    canSeePurchaseOrders ? buildContactsPurchaseOrdersWidget(contactType) : null,
    canManageFinancialDocuments ? buildContactsInvoicesWidget(contactType) : null,
  ])

  const mainContent = (
    <>
      {widgetsList.length != 0 && (
        <WidgetDashboard
          widgets={widgetsList}
          employeeId={employee.id}
          args={{
            contactId: contact.id,
            companyId: contact.companyId,
            toDueDate: moment().subtract(1, 'days').utc().startOf('day').toDate().toISOString(),
          }}
        />
      )}
    </>
  )

  const sideContent = <ContactsDetailsInfo contact={contact} onEditContact={handleEditContact} />

  return <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
}

export default OverviewInformation
